import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import {
  selectSchema,
  selectSchemasChatMode,
  selectSchemasIsFileLoading,
  selectSchemasThreadRef,
  selectSchemaValue,
} from "../redux/selectors";
import { hiddenSecurityPart } from "../../../shared/utils/hiddenSecurityPart";
import { getFileExtension } from "../../Meeting/helpers/getFileExtension";
import {
  FILE_CHAT_META_INSTRUCTION,
  UPLOADED_FILE_EXTENSIONS,
} from "../../../common/Chat/constants";
import { getArtifactContentAction } from "../../Files/redux/actions";
import { getFileConfig } from "../../Files/helpers";
import { useStorageSelectors } from "../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { createNotification } from "../../../app/state/notificationSlice/slice";
import {
  deleteSchemaThreadAction,
  getSchemaMessagesAction,
  postSchemaMessageAction,
  postSchemaMessageToChatCompletion,
  uploadFileToSchemaDialogAction,
} from "../redux/actions";
import { addUserMessage, setSchemaValue } from "../redux/slice";
import {
  SchemasValue,
  TSchemaChatMode,
} from "../../../services/api/instances/schemasApi/types";
import { useUpdateStorageValue } from "../../../app/state/storageObjectSlice/hook/use-update-storage-value";
import { looksLikeXml } from "../helpers/extractXmlFromMarkdown";
import { throttle } from "throttle-debounce";

export const useSchemasChat = (
  loadXml: (xml: string) => void,
  exportAsBase64: () => Promise<string>,
  handleAutosaveThrottle: throttle<(xml: string) => void>,
) => {
  const dispatch = useAppDispatch();
  const value = useAppSelector(selectSchemaValue);
  const schema = useAppSelector(selectSchema);
  const threadRef = useAppSelector(selectSchemasThreadRef);
  const chatMode = useAppSelector(selectSchemasChatMode);
  const isFileLoading = useAppSelector(selectSchemasIsFileLoading);
  const { selectedId, selectedObject } = useStorageSelectors("schemas");
  const { saveValue } = useUpdateStorageValue("schemas", setSchemaValue);
  const [message, setMessage] = useState<string>("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string>("");
  const [withXml, setWithXml] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const uploadAbortFnRef = useRef<(() => void) | null>(null);

  const selectedIdRef = useRef(selectedId);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  const handleChangeMessage = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.target.value);
  };

  const handleUploadCurrentSchema = () => {
    setWithXml(true);
    setFileContent(`${selectedObject?.friendly_name}.drawio`);
  };

  const handleChangeChatMode = (type: TSchemaChatMode) => {
    if (!value) return;
    const updatedValue: SchemasValue = {
      ...value,
      metadata: {
        ...value?.metadata,
        mode: type,
      },
    };
    saveValue(updatedValue);
    if (type === "generate_schema" && threadRef) {
      dispatch(getSchemaMessagesAction({ threadRef, id: selectedId ?? "" }));
    }
  };

  const handleFileUpload = (e: any) => {
    if (!e.target.files[0]) return;
    const file = e.target.files[0];
    setFileName(file.name);
    const fileExtension = getFileExtension(file.name) ?? "";
    if (!UPLOADED_FILE_EXTENSIONS.includes(fileExtension)) {
      dispatch(
        createNotification(
          "warning",
          `Расширение загруженного файла (${fileExtension}) не поддерживается`,
        ),
      );
      setFileName(null);
      setFileContent("");
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      return;
    }

    const promise = dispatch(uploadFileToSchemaDialogAction(file));
    const abortFn = () => promise.abort();
    uploadAbortFnRef.current = abortFn;

    promise
      .unwrap()
      .then((text) => {
        setFileContent(text);
      })
      .catch((error) => {
        if (error !== "Aborted") {
          console.error(error);
        }
      });
  };

  const handleArtifactsClick = useCallback(
    async (hash: string, fileName: string) => {
      const fileConfig = getFileConfig(fileName);
      if (!fileConfig) return;
      const fileExtension = getFileExtension(fileName) ?? "";
      if (!UPLOADED_FILE_EXTENSIONS.includes(fileExtension)) {
        dispatch(
          createNotification(
            "warning",
            `Расширение загруженного файла (${fileExtension}) не поддерживается`,
          ),
        );
        setMessage("");
        setFileName(null);
        setFileContent("");
        if (inputRef.current) {
          inputRef.current.value = "";
        }
        return;
      }
      setFileName(fileName);
      const promise = dispatch(getArtifactContentAction({ hash, fileName }));
      const abortFn = () => promise.abort();
      uploadAbortFnRef.current = abortFn;

      promise
        .unwrap()
        .then((content) => {
          setFileContent(content);
        })
        .catch((error) => {
          if (error !== "Aborted") {
            console.error(error);
          }
        });
    },
    [fileName, fileContent],
  );

  const handleSendMessage = useCallback(async () => {
    if (!value) return;
    if (chatMode === "generate_image") {
      const updatedValue: SchemasValue = {
        ...value,
        schemas_object: {
          ...value?.schemas_object,
          messages: [
            ...(value?.schemas_object?.messages ?? []),
            { role: "user", content: message },
          ],
        },
      };
      dispatch(setSchemaValue(updatedValue));
      const base64 = await exportAsBase64();
      dispatch(
        postSchemaMessageToChatCompletion({
          fileData: base64,
          id: selectedId ?? "",
        }),
      ).then(() => handleAutosaveThrottle.cancel());
      setMessage("");
      return;
    }
    const finalMessage = fileName
      ? `${message} <security>${FILE_CHAT_META_INSTRUCTION} ${fileContent}</security> \n\n<file>${fileName}</file>`
      : `<security>${withXml && schema}</security> ${message}`;
    dispatch(
      addUserMessage(selectedId as string, hiddenSecurityPart(finalMessage)),
    );
    dispatch(
      postSchemaMessageAction({
        message: finalMessage,
        threadRef: threadRef,
        id: selectedId ?? "",
      }),
    )
      .unwrap()
      .then((res) => {
        if (looksLikeXml(res?.xml ?? "") && selectedIdRef.current === res?.id) {
          loadXml(res?.xml ?? "");
        }
      });
    setMessage("");
    setFileName(null);
    setFileContent("");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, [
    message,
    fileName,
    fileContent,
    withXml,
    chatMode,
    value?.schemas_object?.schema_xml,
    value?.schemas_object?.messages,
    selectedId,
    dispatch,
  ]);

  const handleSendKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.ctrlKey && event.key === "Enter") {
        event.preventDefault();
        handleSendMessage();
      }
    },
    [message, fileName, fileContent, withXml, dispatch],
  );

  const handleClearChat = useCallback(() => {
    dispatch(deleteSchemaThreadAction({ threadRef, id: selectedId ?? "" }));
  }, [threadRef, value?.metadata?.mode, selectedId]);

  const handleDeleteFileTag = () => {
    uploadAbortFnRef.current?.();
    setFileName(null);
    setFileContent("");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  return {
    message,
    fileName,
    selectedId,
    isFileLoading,
    inputRef,
    withXml,
    chatMode,
    fileContent,
    selectedObject,
    handleArtifactsClick,
    handleChangeChatMode,
    handleClearChat,
    handleDeleteFileTag,
    handleUploadCurrentSchema,
    handleFileUpload,
    handleChangeMessage,
    handleSendKeyDown,
    handleSendMessage,
  };
};
